/**
 * Haelt die Laufschrift lueckenlos.
 *
 * Die Section rendert zwei identische Gruppen und schiebt die Spur pro Runde um
 * genau eine davon weiter. Nahtlos ist das aber nur, solange eine Gruppe
 * mindestens so breit ist wie das Fenster. Bei drei kurzen Meldungen ist sie das
 * nicht: gegen Ende jeder Runde war die Spur schmaler als der sichtbare
 * Ausschnitt, die Leiste lief leer und fuellte sich beim Ansetzen der
 * Wiederholung schlagartig wieder – genau der Aussetzer, der wie ein Fehler
 * aussieht.
 *
 * Diese Komponente legt so viele Kopien nach, dass hinter der auslaufenden
 * Gruppe immer noch eine volle Fensterbreite Inhalt steht, und teilt der
 * Animation ueber --am-copies mit, um welchen Bruchteil sie schieben muss.
 */
class AnnouncementMarquee extends HTMLElement {
  /** Unveraenderte Vorlage der ersten Gruppe, aus der die Kopien entstehen. */
  #template = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    const first = this.#track?.firstElementChild;
    if (!(first instanceof HTMLElement)) return;

    this.#template = /** @type {HTMLElement} */ (first.cloneNode(true));
    // Kopien sind reine Wiederholung – Screenreader lesen nur die erste Gruppe.
    this.#template.setAttribute('aria-hidden', 'true');

    this.#fill();

    this.#resizeObserver = new ResizeObserver(() => this.#fill());
    this.#resizeObserver.observe(this);

    // Bis die Schrift geladen ist, misst die Gruppe in der Ersatzschrift und
    // faellt danach anders aus. Ohne dieses Nachrechnen bliebe die Anzahl auf
    // dem Wert der Ersatzschrift stehen.
    document.fonts?.ready.then(() => this.#fill()).catch(() => {});
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('.announcement-marquee__track');
  }

  #fill() {
    const track = this.#track;
    const first = track?.firstElementChild;

    if (!track || !(first instanceof HTMLElement) || !this.#template) return;

    const groupWidth = first.getBoundingClientRect().width;
    const viewportWidth = this.getBoundingClientRect().width;

    // Ohne Breite – Leiste ausgeblendet, Schrift noch nicht da – waere jede
    // Rechnung Unsinn. Dann lieber nichts tun und es beim naechsten Anlass
    // erneut versuchen.
    if (groupWidth < 1 || viewportWidth < 1) return;

    // Eine Gruppe wandert pro Runde aus dem Bild; was nachrueckt, muss das
    // Fenster fuellen. Also: Fenster durch Gruppenbreite, aufgerundet, plus die
    // eine wandernde Gruppe. Mindestens zwei, sonst gaebe es nichts zu wiederholen.
    const needed = Math.max(2, Math.ceil(viewportWidth / groupWidth) + 1);
    const current = track.children.length;

    if (needed === current) return;

    if (needed > current) {
      const fragment = document.createDocumentFragment();
      for (let i = current; i < needed; i++) fragment.append(this.#template.cloneNode(true));
      track.append(fragment);
    } else {
      for (let i = current; i > needed; i--) track.lastElementChild?.remove();
    }

    track.style.setProperty('--am-copies', String(needed));
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
